import {HttpParams} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {ApiResponse, BreadcrumbDto, Catalog, CompoApiService, Environment, LazyCatalog} from 'compob2b-libs';
import {Observable, Subject} from 'rxjs';
import {map, share} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';

@Injectable({
  providedIn: 'root'
})
export class CatalogService<T extends LazyCatalog = LazyCatalog> {
  readonly reloadCatalogSubject = new Subject<boolean>();

  enviropment = inject(Environment);

  constructor(protected readonly compoApiService: CompoApiService) {
  }

  get apiUrl(): string {
    return this.enviropment.accessRolesCatalog ? '/catalog/roles' : '/catalog';
  }

  reloadCatalog(): void {
    this.reloadCatalogSubject.next(true);
  }

  isReloading(): Observable<boolean> {
    return this.reloadCatalogSubject.asObservable();
  }

  // getCatalogTree(): Observable<Catalog[]> {
  //   const tree: Catalog[] = [{id: 1, header: translations.root, disabled: true}];
  //
  //   return this.compoApiService.get<Catalog[]>(this.apiUrl)
  //     .pipe(
  //       map(response => flatTreeTransform<Catalog>(response.data, tree, translations.root, 'children'))
  //     );
  // }

  getLazyCatalogTree(): Observable<T[]> {
    return this.compoApiService.get<T[]>(`${this.apiUrl}/lazy`)
      .pipe(
        share(),
        map(response => response.data),
      );
  }

  initCatalogTree(catalogId: number, showDeletedDisabled: boolean = false): Observable<T[]> {
    const params = new HttpParams().set('showDeletedDisabled', showDeletedDisabled);

    return this.compoApiService
      .getWithParams<T[]>(catalogId ? `${this.apiUrl}/root-lazy/${catalogId}` : `${this.apiUrl}/root`, params)
      .pipe(map(response => response.data));
  }

  getLazyChildren(id: number, showDeletedDisabled: boolean = false): Observable<T[]> {
    if (showDeletedDisabled) {
      return this.getAllLazyChildren(id, true);
    }

    return this.compoApiService.get<T[]>(`${this.apiUrl}/children/${id}`)
      .pipe(
        map(response => response.data)
      );
  }

  getAllLazyChildren(id: number, showDeleted: boolean): Observable<T[]> {
    const params = new HttpParams().set('showDeletedDisabled', showDeleted);
    return this.compoApiService.getWithParams<T[]>(`${this.apiUrl}/children/` + id, params)
      .pipe(
        map(response => response.data)
      );
  }

  // getCatalog(): Observable<Catalog[]> {
  //   return this.compoApiService.get<Catalog[]>(this.apiUrl)
  //     .pipe(map(response => response.data));
  // }

  searchCatalogsByHeader(term: string): Observable<Catalog[]> {
    const url = `${this.apiUrl}/search`;

    const params = new HttpParams().set('header', term);

    return this.compoApiService.getWithParams<Catalog[]>(url, params).pipe(
      map(response => response.data)
    );
  }

  getCatalogBreadCrumbs(id: number): Observable<BreadcrumbDto[]> {
    const uri = `/product/catalog-parent-chain/${id}`;
    return this.compoApiService.get<BreadcrumbDto[]>(uri)
      .pipe(
        map(response => {
          return [
            {id: null, header: translations.catalog},
            ...response.data
          ];
        }),
      );
  }

  isLastCatalog(id: number): Observable<boolean> {
    return this.compoApiService.get<boolean>(`${this.apiUrl}/last/` + id)
      .pipe(
        map(response => response.data)
      );
  }

  addChildrenToParentByParentId(id: number, node, childNodes) {
    let isSuccess = false;

    const {children} = node;
    if (node.id == id) {
      childNodes = [].concat(childNodes);
      node.children =
        ((Array.isArray(children) && children) || []).filter(child =>
          childNodes.every(node => node.id !== child.id)
        ).concat(childNodes);
      isSuccess = true;
    } else if (Array.isArray(children)) {
      isSuccess = children.some(child =>
        this.addChildrenToParentByParentId(id, child, childNodes)
      );
    }
    return isSuccess;
  }

  treeRecursion(tree: T[], roots: T[]) {
    roots.forEach(item => {
      const childs = tree.filter(child => child.parentId == item.id);
      if (childs.length) {
        item.expanded = true;
        this.addChildrenToParentByParentId(item.id, item, childs);
        this.treeRecursion(tree, childs);
      }
    });
  }

  getParent(root: T[], id: number): T {
    let nodes;
    root.some((n) => {
      if (n.id == id) {
        return nodes = n;
      }
      if (n.children) {
        return nodes = this.getParent(n.children, id);
      }
    });
    return nodes || null;
  }

  addChildrenToTree(tree: T[], parentId: number, children) {
    const updateNode = (node) => {
      if (node.id === parentId) {
        const existingChildren = node.children || [];
        const filteredChildren = existingChildren.filter(existing =>
          !children.some(newChild => newChild.id === existing.id)
        );
        return {
          ...node,
          children: [...filteredChildren, ...children],
          expanded: true
        };
      }

      if (node.children) {
        return {
          ...node,
          children: node.children.map(updateNode)
        };
      }

      return node;
    };

    return tree.map(updateNode);
  }

  deleteChildren(tree: T[], catalogToDelete: T) {
    const parentCatalog = this.getParent(tree, catalogToDelete.parentId);
    if (!parentCatalog) {
      return tree.filter(item => item.id !== catalogToDelete.id);
    } else {
      parentCatalog.children = parentCatalog.children.filter(item => item.id !== catalogToDelete.id);

      return [...tree];
    }

  }

  updatePositions(changes: { id: number; newParentId: number; newPosition: number }[]): Observable<ApiResponse<void>> {
    // NOTE: positions endpoint is always /catalog/positions, regardless of accessRolesCatalog
    return this.compoApiService.put<{ id: number; newParentId: number; newPosition: number }[], void>(`/catalog/positions`, changes);
  }

  get width(): number {
    let width = Number(localStorage.getItem('CatalogLazyWidth'));
    return width > 10 ? width : 310;
  }

  set width(width: number) {
    localStorage.setItem('CatalogLazyWidth', width.toString());
  }

}
